<script setup>
import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ButtonNext from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Logo from 'next/icon/Logo.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useSidebarDropdownAnchor } from './useSidebarDropdownAnchor';

defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();

// Fica no topo da sidebar, então abre pra baixo.
const { triggerRef, anchorStyle, menuMaxHeight, measure } =
  useSidebarDropdownAnchor({ placement: 'bottom' });

// SiteUp E4.3/fix: teleportado pro <body>, o Transition do DropdownContainer
// anima uma casca vazia — a animação roda aqui. O isOpen vem como PROP DO
// SLOT default (ver template): useDropdownContext() aqui no pai do container
// explodia no setup (inject não enxerga provide de um filho) e derrubava o
// componente inteiro — era isso que sumia com o switcher.
const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');

const userAccounts = useMapGetter('getUserAccounts');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};
</script>

<template>
  <DropdownContainer :ignore-click-outside="['[data-sidebar-account-menu]']">
    <template #trigger="{ toggle, isOpen }">
      <!-- Collapsed view: Logo trigger -->
      <button
        v-if="isCollapsed"
        ref="triggerRef"
        class="grid flex-shrink-0 place-content-center p-2 rounded-xl cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-1': isOpen }"
        :title="currentAccount.name"
        @click="
          () => {
            measure();
            toggle();
          }
        "
      >
        <Logo class="size-7" />
      </button>
      <!-- Expanded view: Account name trigger -->
      <button
        v-else
        id="sidebar-account-switcher"
        ref="triggerRef"
        :data-account-id="accountId"
        aria-haspopup="listbox"
        aria-controls="account-options"
        class="flex items-center gap-2 justify-between w-full rounded-xl px-2"
        :class="[
          isOpen && 'bg-n-alpha-1',
          showAccountSwitcher
            ? 'hover:bg-n-alpha-1 cursor-pointer'
            : 'cursor-default',
        ]"
        @click="
          () => {
            if (!showAccountSwitcher) return;
            measure();
            toggle();
          }
        "
      >
        <span
          class="text-sm font-medium leading-5 text-n-slate-12 truncate"
          aria-live="polite"
        >
          {{ currentAccount.name }}
        </span>

        <span
          v-if="showAccountSwitcher"
          aria-hidden="true"
          class="i-ri-arrow-down-s-line size-4 text-n-slate-10 flex-shrink-0"
        />
      </button>
    </template>
    <template #default="{ isOpen }">
      <TeleportWithDirection>
        <Transition
          appear
          enter-active-class="transition duration-[var(--motion-duration-enter)] ease-[var(--motion-ease-out)] origin-top"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-[var(--motion-duration-press)] ease-[var(--motion-ease-out)] origin-top"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="isOpen"
            data-sidebar-account-menu
            class="fixed z-[100]"
            :style="anchorStyle"
          >
            <DropdownBody
              v-if="showAccountSwitcher || isCollapsed"
              class="overflow-y-auto top-0 min-w-80 ltr:left-0 rtl:right-0"
              :style="{ maxHeight: menuMaxHeight }"
            >
              <DropdownSection :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')">
                <DropdownItem
                  v-for="account in sortedCurrentUserAccounts"
                  :id="`account-${account.id}`"
                  :key="account.id"
                  class="cursor-pointer"
                  @click="onChangeAccount(account.id)"
                >
                  <template #label>
                    <div
                      :for="account.name"
                      class="text-left rtl:text-right flex gap-2 items-center"
                    >
                      <span
                        class="text-n-slate-12 max-w-36 truncate min-w-0"
                        :title="account.name"
                      >
                        {{ account.name }}
                      </span>
                      <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
                      <span
                        class="text-n-slate-11 max-w-24 truncate capitalize"
                        :title="account.name"
                      >
                        {{
                          account.custom_role_id
                            ? account.custom_role.name
                            : account.role
                        }}
                      </span>
                    </div>
                    <Icon
                      v-show="account.id === accountId"
                      icon="i-ri-check-line"
                      class="text-n-teal-11 size-5"
                    />
                  </template>
                </DropdownItem>
              </DropdownSection>
              <DropdownItem v-if="globalConfig.createNewAccountFromDashboard">
                <ButtonNext
                  color="slate"
                  variant="faded"
                  class="w-full"
                  size="sm"
                  @click="emitNewAccount"
                >
                  {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
                </ButtonNext>
              </DropdownItem>
            </DropdownBody>
          </div>
        </Transition>
      </TeleportWithDirection>
    </template>
  </DropdownContainer>
</template>
